<template>
  <!-- Sun Effect -->
  <div v-if="isSunnyMode" class="sun-effect">
    <div class="sun"></div>
    <div class="rays"></div>
    <div class="cloud cloud-1"></div>
    <div class="cloud cloud-2"></div>
    <div class="cloud cloud-3"></div>
    <div class="cloud cloud-4"></div>
    <div class="cloud cloud-5"></div>
    
    <!-- Dynamic clouds container -->
    <div ref="dynamicCloudsContainer"></div>
  </div>
  
  <!-- Rain Effect -->
  <div v-if="!isSunnyMode" class="rain-effect">
    <div v-for="n in 100" :key="n" class="rain-drop" :style="randomRainDropStyle()"></div>
  </div>
</template>

<script>
import { mapState } from 'pinia'
import { useThemeStore } from '../stores/theme'

export default {
  name: 'BackgroundEffect',
  data() {
    return {
      cloudInterval: null
    }
  },
  computed: {
    ...mapState(useThemeStore, ['isSunnyMode'])
  },
  methods: {
    randomRainDropStyle() {
      const left = `${Math.random() * 100}%`;
      const animationDelay = `${Math.random() * 5}s`;
      const animationDuration = `${Math.random() * 5 + 5}s`;
      return {
        left,
        animationDelay,
        animationDuration
      };
    },
    createCloud() {
      if (!this.$refs.dynamicCloudsContainer || !this.isSunnyMode) return;
      
      // Create a new cloud element
      const cloud = document.createElement('div');
      cloud.className = 'dynamic-cloud';
      
      // Random cloud dimensions
      const width = Math.floor(Math.random() * 120) + 100; // 100-220px width
      const height = Math.floor(Math.random() * 30) + 40; // 40-70px height
      
      // Random position (vertical only, horizontal will be offscreen)
      const top = Math.floor(Math.random() * window.innerHeight);
      
      // Random animation duration (speed)
      const duration = Math.floor(Math.random() * 60) + 60; // 60-120s duration
      
      // Set cloud styles
      cloud.style.width = `${width}px`;
      cloud.style.height = `${height}px`;
      cloud.style.top = `${top}px`;
      cloud.style.animationDuration = `${duration}s`;
      
      // Add cloud to container
      this.$refs.dynamicCloudsContainer.appendChild(cloud);
      
      // Remove cloud after animation completes
      setTimeout(() => {
        if (cloud.parentNode) {
          cloud.parentNode.removeChild(cloud);
        }
      }, duration * 1000);
    },
    startCloudGenerator() {
      // Generate a new cloud every 10-30 seconds
      if (this.isSunnyMode) {
        this.cloudInterval = setInterval(() => {
          this.createCloud();
        }, Math.random() * 20000 + 10000); // 10-30 seconds
      }
    },
    stopCloudGenerator() {
      clearInterval(this.cloudInterval);
    }
  },
  watch: {
    isSunnyMode(newValue) {
      if (newValue) {
        this.$nextTick(() => {
          this.startCloudGenerator();
        });
      } else {
        this.stopCloudGenerator();
      }
    }
  },
  mounted() {
    if (this.isSunnyMode) {
      this.$nextTick(() => {
        this.startCloudGenerator();
      });
    }
  },
  beforeUnmount() {
    this.stopCloudGenerator();
  }
}
</script>

<style lang="scss">
/* Sun Effect */
.sun-effect {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
}

.sun {
  position: fixed;
  top: -40px;
  right: -40px;
  width: 120px;
  height: 120px;
  background: radial-gradient(circle at center, #ffeb3b, #f8c630);
  border-radius: 50%;
  box-shadow: 0 0 60px rgba(248, 198, 48, 0.8), 0 0 100px rgba(248, 198, 48, 0.5);
  animation: pulse 5s infinite alternate;
}

.rays {
  position: fixed;
  top: -40px;
  right: -40px;
  width: 120px;
  height: 120px;
  background: transparent;
  border-radius: 50%;
  box-shadow: 0 0 0 20px rgba(248, 198, 48, 0.1), 0 0 0 40px rgba(248, 198, 48, 0.05), 0 0 0 60px rgba(248, 198, 48, 0.025);
  animation: rays 20s infinite linear;
}

.cloud {
  position: absolute;
  background: linear-gradient(to bottom, rgba(220, 225, 235, 0.95), rgba(200, 210, 225, 0.85));
  border-radius: 50px;
  animation: float linear infinite;
  opacity: 0.85;
  filter: blur(2px);
  box-shadow: inset 0 5px 15px rgba(255, 255, 255, 0.4),
              inset 0 -5px 15px rgba(180, 195, 210, 0.5),
              0 5px 10px rgba(0, 0, 0, 0.1);
}

.cloud-1 {
  width: 180px;
  height: 60px;
  top: 120px;
  right: -180px;
  animation-duration: 90s;
  animation-delay: 0s;
}

.cloud-2 {
  width: 140px;
  height: 40px;
  top: 80px;
  right: -140px;
  animation-duration: 60s;
  animation-delay: 15s;
}

.cloud-3 {
  width: 220px;
  height: 70px;
  top: 200px;
  right: -220px;
  animation-duration: 120s;
  animation-delay: 30s;
}

.cloud-4 {
  width: 160px;
  height: 50px;
  top: 280px;
  right: -160px;
  animation-duration: 75s;
  animation-delay: 5s;
}

.cloud-5 {
  width: 190px;
  height: 60px;
  top: 40px;
  right: -190px;
  animation-duration: 110s;
  animation-delay: 45s;
}

.cloud::before,
.cloud::after {
  content: '';
  position: absolute;
  background: inherit;
  border-radius: inherit;
}

.cloud::before {
  width: 50%;
  height: 100%;
  top: -50%;
  left: 25%;
}

.cloud::after {
  width: 30%;
  height: 100%;
  top: -30%;
  left: 60%;
}

@keyframes float {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-2000px);
  }
}

@keyframes pulse {
  0% { transform: scale(1); filter: brightness(1); }
  100% { transform: scale(1.1); filter: brightness(1.2); }
}

@keyframes rays {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Dynamic Cloud System */
.dynamic-cloud {
  position: fixed;
  background: linear-gradient(to bottom, rgba(220, 225, 235, 0.95), rgba(200, 210, 225, 0.85));
  border-radius: 50px;
  opacity: 0.9;
  filter: blur(3px);
  z-index: 0;
  pointer-events: none;
  animation: float-dynamic linear forwards;
  box-shadow: inset 0 5px 15px rgba(255, 255, 255, 0.4), 
              inset 0 -5px 15px rgba(180, 195, 210, 0.5), 
              0 5px 10px rgba(0, 0, 0, 0.1);
}

.dynamic-cloud::before,
.dynamic-cloud::after {
  content: '';
  position: absolute;
  background: inherit;
  border-radius: inherit;
}

.dynamic-cloud::before {
  width: 50%;
  height: 100%;
  top: -50%;
  left: 25%;
}

.dynamic-cloud::after {
  width: 30%;
  height: 100%;
  top: -30%;
  left: 60%;
}

@keyframes float-dynamic {
  0% {
    transform: translateX(100vw);
  }
  100% {
    transform: translateX(-100%);
  }
}

/* Rain Effect */
.rain-effect {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.rain-drop {
  position: absolute;
  top: -20px;
  width: 2px;
  height: 20px;
  background: linear-gradient(to bottom, rgba(52, 152, 219, 0), rgba(52, 152, 219, 0.6));
  filter: blur(1px);
  animation: rain linear infinite;
}

@keyframes rain {
  0% {
    transform: translateY(0) translateX(0) rotate(15deg);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(100vh) translateX(-30vh) rotate(15deg);
    opacity: 0;
  }
}
</style> 